import React, { useState, useEffect } from 'react';
import { Property } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import { formatPhone } from '../utils/masks';
import { translateAmenity } from '../utils/translations';
import ImageModal from './ImageModal';
import { useToast } from '../hooks/use-toast';
import { useCondominium } from '../context/CondominiumContext';
import { PropertyImage } from '../utils/imageUtils';
import { PropertyQualityScore } from './PropertyQualityScore';
import { PropertyMarketAnalysis } from './PropertyMarketAnalysis';
import { ExternalMarketAnalysis } from './ExternalMarketAnalysis';
import { PropertyPerformanceDashboard } from './PropertyPerformanceDashboard';
import { 
  X, 
  MapPin, 
  Building, 
  Calendar, 
  Tag,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Bed,
  Car,
  Ruler,
  Bath,
  Star,
  Crown,
  DollarSign,
  User,
  Phone,
  Mail,
  Key,
  Share2
} from 'lucide-react';

interface PropertyDetailModalProps {
  property: Property;
  onClose: () => void;
}

const PropertyDetailModal: React.FC<PropertyDetailModalProps> = ({ property, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [showImageModal, setShowImageModal] = React.useState(false);
  const [allImages, setAllImages] = useState<PropertyImage[]>([]);
  const { filterPropertyData, canViewOwnerData, isDirector, isManagerOrDirector, isCapturer } = usePermissions();
  const { toast } = useToast();
  const { getCondominiumById } = useCondominium();
  
  // Aplica filtro de permissões aos dados do imóvel
  const filteredProperty = filterPropertyData(property);
  const canViewOwner = canViewOwnerData(property);

  // Carregar imagens do condomínio + imóvel
  useEffect(() => {
    const loadImages = () => {
      let images: PropertyImage[] = property.media?.images || [];
      
      console.log('🏢 PropertyDetailModal - Carregando imagens para propriedade:', property.id);
      console.log('📸 PropertyDetailModal - Imagens do imóvel:', images.length);
      
      // Se tem condomínio, carregar as imagens dele também
      if (property.condominiumId) {
        const condominium = getCondominiumById(property.condominiumId);
        if (condominium?.images?.length) {
          const condImages: PropertyImage[] = condominium.images.map(img => ({
            id: `cond_${img.id}`,
            url: img.url,
            name: `${condominium.name} - ${img.name}`,
            primary: false
          }));
          
          console.log('🏢 PropertyDetailModal - Imagens do condomínio:', condImages.length);
          images = [...images, ...condImages];
        }
      }
      
      console.log('📸 PropertyDetailModal - Total de imagens carregadas:', images.length);
      setAllImages(images);
    };

    loadImages();
  }, [property.id, property.condominiumId, property.media?.images, getCondominiumById]);
  
  // Debug logs para verificar permissões
  console.log('PropertyDetailModal Debug:', {
    isDirector: isDirector(),
    isManagerOrDirector: isManagerOrDirector(),
    isCapturer: isCapturer(property),
    canViewOwner,
    propertyUserId: property.userId
  });

  // Debug das imagens
  console.log('🖼️ PropertyDetailModal - Imagens encontradas:', property.media.images);

  const nextImage = () => {
    if (allImages && allImages.length > 1) {
      setCurrentImageIndex((prev) => 
        prev === allImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (allImages && allImages.length > 1) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? allImages.length - 1 : prev - 1
      );
    }
  };

  const handleImageClick = () => {
    setShowImageModal(true);
  };

  const handleImageModalNext = () => {
    if (allImages && allImages.length > 1) {
      setCurrentImageIndex((prev) => 
        prev === allImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handleImageModalPrev = () => {
    if (allImages && allImages.length > 1) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? allImages.length - 1 : prev - 1
      );
    }
  };

  // Função local para formatação de preços
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/imovel/${property.listingId}`;
    const title = property.title || `Imóvel ${property.propertyCode}`;
    const text = `Confira este imóvel: ${title}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        toast({ title: 'Compartilhado', description: 'Link compartilhado com sucesso!' });
      } else {
        await navigator.clipboard.writeText(url);
        toast({ title: 'Link copiado', description: 'URL do imóvel copiada para a área de transferência.' });
      }
    } catch (err) {
      try {
        await navigator.clipboard.writeText(url);
        toast({ title: 'Link copiado', description: 'URL do imóvel copiada para a área de transferência.' });
      } catch {
        alert(url);
      }
    }
  };

  const categoryColors = {
    'Luxo': 'bg-purple-100 text-purple-800 border-purple-200',
    'Alto Padrão': 'bg-blue-100 text-blue-800 border-blue-200',
    'Médio/Alto': 'bg-green-100 text-green-800 border-green-200',
    'Médio': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Econômico': 'bg-gray-100 text-gray-800 border-gray-200'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[98vh] sm:max-h-[95vh] lg:max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{property.title}</h2>
            <div className="flex items-center space-x-3 mt-2">
              <span className="text-sm text-gray-500">Código: {property.propertyCode}</span>
              <span className={`text-sm px-2 py-1 rounded-full border ${categoryColors[property.category]}`}>
                {property.category}
              </span>
              {property.publicationType === 'PREMIUM' && (
                <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-sm border border-orange-200 flex items-center">
                  <Crown className="w-3 h-3 mr-1" />
                  Premium
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleShare}
              className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Compartilhar"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Fechar"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Images Section */}
          {allImages && allImages.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <ImageIcon className="w-5 h-5 mr-2 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-800">Imagens</h3>
                <span className="ml-2 text-sm text-gray-500">
                  ({allImages.length} foto{allImages.length > 1 ? 's' : ''})
                </span>
              </div>
              <div className="relative">
                <img
                  src={allImages[currentImageIndex].preview || allImages[currentImageIndex].url}
                  alt={`${property.title} - Imagem ${currentImageIndex + 1}`}
                  className="w-full h-80 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={handleImageClick}
                />
                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                      {currentImageIndex + 1} / {allImages.length}
                    </div>
                  </>
                )}
              </div>
              {allImages.length > 1 && (
                <div className="flex gap-2 mt-4 overflow-x-auto">
                  {allImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                        index === currentImageIndex ? 'border-orange-500' : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={image.preview || image.url}
                        alt={`Miniatura ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Property Details */}
              <div>
                <div className="flex items-center mb-4">
                  <Building className="w-5 h-5 mr-2 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Detalhes do Imóvel</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center text-gray-600 text-sm mb-1">
                      <Bed className="w-4 h-4 mr-1" />
                      <span>Quartos</span>
                    </div>
                    <p className="text-gray-800 font-medium">{property.bedrooms}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center text-gray-600 text-sm mb-1">
                      <Star className="w-4 h-4 mr-1" />
                      <span>Suítes</span>
                    </div>
                    <p className="text-gray-800 font-medium">{property.suites}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center text-gray-600 text-sm mb-1">
                      <Bath className="w-4 h-4 mr-1" />
                      <span>Banheiros</span>
                    </div>
                    <p className="text-gray-800 font-medium">{property.bathrooms}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center text-gray-600 text-sm mb-1">
                      <Car className="w-4 h-4 mr-1" />
                      <span>Vagas</span>
                    </div>
                    <p className="text-gray-800 font-medium">{property.garage.quantity}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center text-gray-600 text-sm mb-1">
                      <Ruler className="w-4 h-4 mr-1" />
                      <span>Área</span>
                    </div>
                    <p className="text-gray-800 font-medium">{property.livingArea}m²</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center text-gray-600 text-sm mb-1">
                      <Building className="w-4 h-4 mr-1" />
                      <span>Tipo</span>
                    </div>
                    <p className="text-gray-800 font-medium">{property.propertyType}</p>
                  </div>
                </div>
              </div>

              {/* Prices */}
              <div>
                <div className="flex items-center mb-4">
                  <DollarSign className="w-5 h-5 mr-2 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Valores</h3>
                </div>
                <div className="space-y-3">
                  {/* Preço de Venda - sempre exibir se transactionType inclui venda */}
                  {(property.transactionType === 'Venda' || property.transactionType === 'Venda/Aluguel') && (
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="text-green-600 text-sm mb-1">Preço de Venda</div>
                      <p className="text-green-800 font-bold text-xl">{formatPrice(property.listPrice)}</p>
                    </div>
                  )}
                  
                  {/* Preço de Aluguel - sempre exibir se transactionType inclui aluguel */}
                  {(property.transactionType === 'Aluguel' || property.transactionType === 'Venda/Aluguel') && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="text-blue-600 text-sm mb-1">Preço de Aluguel</div>
                      <p className="text-blue-800 font-bold text-xl">
                        {property.rentalPrice ? formatPrice(property.rentalPrice) : 'Não informado'}
                      </p>
                    </div>
                  )}
                  
                  {/* Condomínio - sempre exibir */}
                  <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                    <div className="text-orange-600 text-sm mb-1">Condomínio (mensal)</div>
                    <p className="text-orange-800 font-medium">
                      {property.propertyAdministrationFee ? formatPrice(property.propertyAdministrationFee) : 'Não informado'}
                    </p>
                  </div>
                  
                  {/* IPTU - sempre exibir */}
                  <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                    <div className="text-yellow-600 text-sm mb-1">IPTU (mensal)</div>
                    <p className="text-yellow-800 font-medium">
                      {property.yearlyTax ? formatPrice(property.yearlyTax) : 'Não informado'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Address */}
              <div>
                <div className="flex items-center mb-4">
                  <MapPin className="w-5 h-5 mr-2 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Localização</h3>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-800 font-medium">
                    {property.location.address}, {property.location.streetNumber}
                  </p>
                  {property.tower && (
                    <p className="text-gray-700 text-sm bg-blue-50 px-2 py-1 rounded mt-1 inline-block">
                      Torre: {property.tower}
                    </p>
                  )}
                  {property.location.complement && (
                    <p className="text-gray-700 text-sm bg-green-50 px-2 py-1 rounded mt-1 inline-block">
                      Número: {property.location.complement}
                    </p>
                  )}
                  <p className="text-gray-600">
                    {property.location.neighborhood} - {property.location.city}, {property.location.state}
                  </p>
                  <p className="text-gray-500 text-sm">
                    CEP: {property.location.postalCode}
                  </p>
                </div>
              </div>

              {/* Proprietário */}
              <div>
                <div className="flex items-center mb-4">
                  <User className="w-5 h-5 mr-2 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Proprietário</h3>
                  {!canViewOwner && (
                    <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                      Acesso restrito
                    </span>
                  )}
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-800 font-medium">{filteredProperty.owner.name}</p>
                  <div className="flex items-center text-gray-600 text-sm mt-2">
                    <Mail className="w-4 h-4 mr-1" />
                    <span>{filteredProperty.owner.email}</span>
                  </div>
                  <div className="flex items-center text-gray-600 text-sm mt-1">
                    <Phone className="w-4 h-4 mr-1" />
                    <span>{formatPhone(filteredProperty.owner.phone)}</span>
                  </div>
                  {canViewOwner && (
                    <div className="flex items-center text-gray-600 text-sm mt-1">
                      <Key className="w-4 h-4 mr-1" />
                      <span>Chaves: {property.keysLocation}</span>
                    </div>
                  )}
                  {!canViewOwner && (
                    <div className="mt-2 text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
                      📋 Dados visíveis apenas para o captador ou gerência
                    </div>
                  )}
                </div>
              </div>

              {/* Corretor Responsável */}
              <div>
                <div className="flex items-center mb-4">
                  <User className="w-5 h-5 mr-2 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Corretor Responsável</h3>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-800 font-medium">{property.contact.brokerName}</p>
                  <div className="flex items-center text-gray-600 text-sm mt-2">
                    <Mail className="w-4 h-4 mr-1" />
                    <span>{property.contact.brokerEmail}</span>
                  </div>
                  <div className="flex items-center text-gray-600 text-sm mt-1">
                    <Phone className="w-4 h-4 mr-1" />
                    <span>{formatPhone(property.contact.brokerPhone)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Features */}
          {property.features && property.features.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center mb-4">
                <Tag className="w-5 h-5 mr-2 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-800">Características</h3>
                <span className="ml-2 text-sm text-gray-500">
                  ({property.features.length} item{property.features.length > 1 ? 's' : ''})
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {property.features.map((feature, index) => (
                  <div
                    key={index}
                    className="bg-orange-50 border border-orange-200 text-orange-800 px-3 py-2 rounded-lg text-sm font-medium text-center"
                  >
                    {translateAmenity(feature)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {property.description && (
            <div className="mt-8">
              <div className="flex items-center mb-4">
                <Building className="w-5 h-5 mr-2 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-800">Descrição</h3>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-800 whitespace-pre-wrap">{property.description}</p>
              </div>
            </div>
          )}

          {/* Info Section */}
          <div className="mt-8">
            <div className="flex items-center mb-4">
              <Calendar className="w-5 h-5 mr-2 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-800">Informações do Sistema</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-gray-600 text-sm mb-1">Data de Listagem</div>
                <p className="text-gray-800 font-medium">
                  {new Date(property.listDate).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-gray-600 text-sm mb-1">Última Atualização</div>
                <p className="text-gray-800 font-medium">
                  {new Date(property.lastUpdateDate).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-gray-600 text-sm mb-1">Tipo de Transação</div>
                <p className="text-gray-800 font-medium">{property.transactionType}</p>
              </div>
            </div>
            <div className="mt-4 bg-gray-50 p-4 rounded-lg">
              <div className="text-gray-600 text-sm mb-1">ID do Imóvel</div>
              <p className="text-gray-800 font-mono text-sm">{property.listingId}</p>
            </div>
          </div>

          {/* Intelligence Section */}
          <div className="mt-8 space-y-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Análises Inteligentes</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PropertyQualityScore propertyId={property.id} autoAnalyze />
              <PropertyMarketAnalysis propertyId={property.id} />
            </div>
            
            <ExternalMarketAnalysis propertyId={property.id} autoAnalyze={false} />
            <PropertyPerformanceDashboard propertyId={property.id} />
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && allImages && allImages.length > 0 && (
        <ImageModal
          isOpen={showImageModal}
          onClose={() => setShowImageModal(false)}
          imageUrl={allImages[currentImageIndex].url || allImages[currentImageIndex].preview || ''}
          title={`${property.title} - Imagem ${currentImageIndex + 1}`}
          currentIndex={currentImageIndex}
          totalImages={allImages.length}
          onNext={allImages.length > 1 ? handleImageModalNext : undefined}
          onPrev={allImages.length > 1 ? handleImageModalPrev : undefined}
        />
      )}
    </div>
  );
};

export default PropertyDetailModal;